/**
 * Token usage tracking and the OTEL token & cost pipeline endpoints.
 *
 * Uncached at this layer — TanStack Query owns response caching and
 * invalidation at the hook layer (useTokenAnalyticsQuery,
 * useTokenForecasting, …), with WebSocket-driven
 * invalidation wired in tokenEventHandler.ts.
 */

import type {
  TokenAggregationPeriod,
  TokenAnalytics,
  TokenCostAnalysis,
  TokenUsageFilters,
  TokenUsageResponse,
} from '@/types/tokenUsage'
import type {
  CorrelationHealthResponse,
  CostForecast,
  ForecastScope,
  TokenForecast,
} from '@/types/tokenAnalytics'
import type { CliSystem, SessionTokenStatsResponse } from '@/types/tokens'
import type {
  ModelSpendWindow,
  ProviderSpendResponse,
  SessionSpendResponse,
  WorkloadSpendResponse,
} from '@/types/modelSpend'
import { apiClient } from './client'
import { repeatedKeyParamsSerializer } from './queryParams'

export const tokens = {
  // Get token usage events with filtering and pagination
  getUsage: async (params?: TokenUsageFilters): Promise<TokenUsageResponse> => {
    const response = await apiClient.get<TokenUsageResponse>(
      '/api/v1/tokens/usage',
      { params }
    )
    return response.data
  },

  // Get session-specific token usage summary
  getSessionUsage: async (
    sessionId: string,
    cliSystem?: CliSystem
  ): Promise<SessionTokenStatsResponse> => {
    const response = await apiClient.get<SessionTokenStatsResponse>(
      `/api/v1/tokens/usage/${sessionId}`,
      { params: cliSystem ? { cli_system: cliSystem } : undefined }
    )
    return response.data
  },

  // Enhanced session token statistics with context window information
  getSessionTokenStats: async (
    sessionId: string,
    cliSystem?: CliSystem
  ): Promise<SessionTokenStatsResponse> => {
    const response = await apiClient.get<SessionTokenStatsResponse>(
      `/api/v1/tokens/usage/${sessionId}`,
      { params: cliSystem ? { cli_system: cliSystem } : undefined }
    )
    return response.data
  },

  // Get comprehensive analytics with trends and efficiency metrics
  getAnalytics: async (params?: {
    session_id?: string
    project_id?: string
    period?: TokenAggregationPeriod
    start_time?: string
    end_time?: string
  }): Promise<TokenAnalytics> => {
    const response = await apiClient.get<TokenAnalytics>(
      '/api/v1/tokens/analytics',
      { params }
    )
    return response.data
  },

  // Get detailed cost analysis and projections
  getCostAnalysis: async (params?: {
    session_id?: string
    project_id?: string
    start_time?: string
    end_time?: string
  }): Promise<TokenCostAnalysis> => {
    const response = await apiClient.get<TokenCostAnalysis>(
      '/api/v1/tokens/cost',
      { params }
    )
    return response.data
  },

  // ---------------------------------------------------------------------------
  // OTEL token & cost pipeline endpoints
  // ---------------------------------------------------------------------------

  /**
   * Token usage forecast — all CLI systems.
   * GET /api/v1/tokens/forecasting/tokens
   */
  getTokenForecast: async (scope: ForecastScope): Promise<TokenForecast> => {
    const response = await apiClient.get<TokenForecast>(
      '/api/v1/tokens/forecasting/tokens',
      { params: scope }
    )
    return response.data
  },

  /**
   * Effective-cost forecast with basis and coverage (discriminated union).
   * Returns available: false when the scope contains no priced rows.
   * GET /api/v1/tokens/forecasting/cost
   */
  getCostForecast: async (scope: ForecastScope): Promise<CostForecast> => {
    const response = await apiClient.get<CostForecast>(
      '/api/v1/tokens/forecasting/cost',
      { params: scope }
    )
    return response.data
  },

  /**
   * Correlation health — reports prompt-coverage rate for the OTEL pipeline.
   * GET /api/v1/tokens/correlation/health
   */
  getCorrelationHealth: async (): Promise<CorrelationHealthResponse> => {
    const response = await apiClient.get<CorrelationHealthResponse>(
      '/api/v1/tokens/correlation/health'
    )
    return response.data
  },

  // ---------------------------------------------------------------------------
  // Model spend: agent sessions beside the platform's own recorded calls
  // ---------------------------------------------------------------------------

  /**
   * Provider spend by origin, and the two summed under one coverage rule.
   * GET /api/v1/tokens/spend
   */
  getProviderSpend: async (
    window: ModelSpendWindow
  ): Promise<ProviderSpendResponse> => {
    const response = await apiClient.get<ProviderSpendResponse>(
      '/api/v1/tokens/spend',
      {
        params: window,
      }
    )
    return response.data
  },

  /**
   * Every workload the platform spent on, by purpose and egress; narrowed to
   * the named capabilities when any are given.
   * GET /api/v1/tokens/spend/workloads
   */
  getWorkloadSpend: async (
    window: ModelSpendWindow,
    capabilityKeys: readonly string[] = []
  ): Promise<WorkloadSpendResponse> => {
    const response = await apiClient.get<WorkloadSpendResponse>(
      '/api/v1/tokens/spend/workloads',
      {
        params: { ...window, capability_key: [...capabilityKeys] },
        paramsSerializer: repeatedKeyParamsSerializer,
      }
    )
    return response.data
  },

  /**
   * One session's spend per artefact: each chunk, range and overview.
   * GET /api/v1/tokens/spend/sessions/{session_id}
   */
  getSessionSpend: async (sessionId: string): Promise<SessionSpendResponse> => {
    const response = await apiClient.get<SessionSpendResponse>(
      `/api/v1/tokens/spend/sessions/${encodeURIComponent(sessionId)}`
    )
    return response.data
  },
}
